import { MapPin, Phone, User, Package, Truck } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { OrderStatusBadge } from '../../components/OrderStatusBadge'
import { PAY_TYPE_LABELS, type Order } from '@/schemas/supplier'

interface OrderDetailCardProps {
  order: Order
}

export function OrderDetailCard({ order }: OrderDetailCardProps) {
  return (
    <div className="space-y-6">
      {/* 订单状态和时间信息 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">订单信息</CardTitle>
            <OrderStatusBadge status={order.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">订单号：</span>
              <span className="font-mono">{order.order_id}</span>
            </div>
            <div>
              <span className="text-muted-foreground">支付方式：</span>
              <span>{PAY_TYPE_LABELS[order.paid] || '未知'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">下单时间：</span>
              <span>{order.add_time}</span>
            </div>
            {order.paid_time && (
              <div>
                <span className="text-muted-foreground">支付时间：</span>
                <span>{order.paid_time}</span>
              </div>
            )}
            {order.delivery_time && (
              <div>
                <span className="text-muted-foreground">发货时间：</span>
                <span>{order.delivery_time}</span>
              </div>
            )}
          </div>
          {order.mark && (
            <div className="text-sm">
              <span className="text-muted-foreground">买家备注：</span>
              <span>{order.mark}</span>
            </div>
          )}
          {order.remark && (
            <div className="text-sm">
              <span className="text-muted-foreground">商家备注：</span>
              <span>{order.remark}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 收货人信息 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            收货信息
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span>{order.real_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{order.user_phone}</span>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>{order.user_address}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 商品列表 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-4 w-4" />
            商品信息
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {order.cart_info?.map((item, index) => (
              <div key={item.cart_id}>
                {index > 0 && <Separator className="mb-4" />}
                <div className="flex items-start gap-4">
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.product_name}</p>
                    {item.product_attr && (
                      <p className="text-sm text-muted-foreground">{item.product_attr}</p>
                    )}
                    {item.sku && (
                      <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium">¥{(item.price ?? 0).toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">x{item.cart_num ?? 0}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Separator className="my-4" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">商品数量：</span>
              <span>{order.total_num} 件</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">商品总价：</span>
              <span>¥{(order.total_price ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-medium text-base">
              <span>实付金额：</span>
              <span className="text-primary">¥{(order.pay_price ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 物流信息 */}
      {order.delivery_name && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="h-4 w-4" />
              物流信息
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">快递公司：</span>
                <span>{order.delivery_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">快递单号：</span>
                <span className="font-mono">{order.delivery_id}</span>
              </div>
              {order.delivery_code && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">快递编码：</span>
                  <span className="font-mono">{order.delivery_code}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
